import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cloneDeep, maxBy } from 'lodash-es';
import { COUNTRIES } from '@hotjar/country-lib';
import { convertFromNewUiToQueryFormat } from '../../../utils/FormatConverter/FilterConverterFromUiToQuery';
import { useAllEvents } from '../../../domains/events/useAllEvents';
import { useAllGoogleExperiments } from '../../../domains/experiments/useAllGoogleExperiments';
import { useAllUnbounceExperiments } from '../../../domains/experiments/useAllUnbounceExperiments';
import { getDefaultFilterInUiFormat } from '../../../constant';
import type { AddFilterTippyInstance } from '../../StatelessComponent/AddFilter/AddFilter';
import type {
  FilterChainConfig,
  GlobalFilter,
  SingleFilterConfig,
} from '../../../domains/global-filters/model/FilterChain';
import {
  FilterKindEnum,
  UpsellFooterEnum,
} from '../../../domains/global-filters/model/FilterChain';
import type { Segment } from '../../../domains/global-filters/model/segment';
import type { UserAttributeOptionType } from '../../StatelessComponent/SingleFilter/UserAttributeFilter/UserAttributeFilterType';
import { ProductType } from '../../../domains/global-filters/model/Product';
import { FilterChain } from '../../StatelessComponent/FilterChain';
import { FeatureGroupEnum } from '../../../domains/site/model';
import type {
  BaseSite,
  SiteWithAccess,
  SiteWithFeature,
  FeatureGroup,
} from '../../../domains/site/model';
import { GlobalFilterContextObject } from '../GlobalContext';
import { useAllOptimizelyExperiments } from '../../../domains/experiments/useAllOptimizelyExperiments';
import type { IntegrationToken } from '../../../domains/integrations/model';
import type { MinimalDateConstraint } from '../../StatelessComponent/SingleFilter/DateRangeFilter/DateRangeFilterType';
import { START_DATE_OF_CLICKED_DATA_COLLECTION } from '../../StatelessComponent/SingleFilter/ClickedFilter/ClickedFilterConstant';
import { trackGlobalFilterApplied } from '../../../utils/trackHelper';
import type { ContextOfUse } from './StatefulFilterChain';

const LEGACY_FILTERS = new Set([FilterKindEnum.TeamComment]);

export type StatefulFilterChainWithResourcesProps = {
  currentFiltersInUiFormat?: GlobalFilter;
  currentSegment?: Segment;
  currentSite: BaseSite & SiteWithAccess & SiteWithFeature;
  segments?: Segment[];
  userAttributeOptionMap: Map<string, UserAttributeOptionType>;
  onChange: (filtersInUiFormat: GlobalFilter, segmentId?: number) => void;
  onSegmentEdited?: (segment: Segment) => void;
  onUpsellRequest: (featureName: string, kind?: string) => void;
  setAddFilterTippyInstance?: (tippyInstance: AddFilterTippyInstance | null) => void;
  hideSaveSegment?: boolean;
  hideAllFilters?: boolean;
  hideDateRangeFilter?: boolean;
  hideAddFilters?: boolean;
  hideClearAllFilters?: boolean;
  usePrimaryStyledAddFilters?: boolean;
  useToggleGroupDatePicker?: boolean;
  hideAllDateOption?: boolean;
  dateConstraint?: MinimalDateConstraint;
  supportedFilterList: Set<FilterKindEnum>;
  contextOfUse: ContextOfUse;
  isScaleOrScaleTrial?: boolean;
  isFiltersDropdownVisibleByDefault?: boolean;
  autoFocusAddFilter?: boolean;
  doNotTrackFiltersChange?: boolean;
  isScaleTrial?: boolean;
  isFunnelStep?: boolean;
};

export const StatefulFilterChainWithResources = ({
  currentFiltersInUiFormat = getDefaultFilterInUiFormat(),
  currentSegment,
  currentSite,
  segments = [],
  userAttributeOptionMap,
  onChange,
  onSegmentEdited,
  onUpsellRequest,
  setAddFilterTippyInstance,
  hideSaveSegment,
  hideAllFilters,
  hideDateRangeFilter,
  hideAddFilters,
  hideClearAllFilters,
  usePrimaryStyledAddFilters,
  useToggleGroupDatePicker,
  hideAllDateOption = true,
  dateConstraint,
  supportedFilterList,
  contextOfUse,
  isFiltersDropdownVisibleByDefault,
  autoFocusAddFilter,
  doNotTrackFiltersChange,
  isScaleOrScaleTrial = false,
  isScaleTrial = false,
  isFunnelStep = false,
}: StatefulFilterChainWithResourcesProps): JSX.Element => {
  const {
    useTrackResult,
    useTrafficCoverageResult,
    isAllowed,
    getMinimumFeatureGroupForFeature,
    isAllowedForAsk,
    hasFilterSavePermission,
    useFeatureFlag,
    useIntegrationTokens,
  } = useContext(GlobalFilterContextObject);
  const [lastSegment, setLastSegment] = useState<Segment | undefined>(currentSegment);
  const hasOptimizelyFeatureFlag = useFeatureFlag?.('foundation_optimizely_filter');
  const hasErrorReportingFeatureFlag = useFeatureFlag?.('error_reporting') ?? false;

  const {
    isLoading: eventOptionListIsLoading,
    data: eventOptionList,
    refetch: refetchEvents,
  } = useAllEvents(currentSite.id);

  const {
    isLoading: googleExperimentOptionListIsLoading,
    data: googleExperimentOptionList,
    refetch: refetchGoogleExperiments,
  } = useAllGoogleExperiments(currentSite.id);

  const {
    isLoading: optimizelyExperimentsOptionListIsLoading,
    data: optimizelyExperimentsOptionList,
    refetch: refetchOptimizelyExperiments,
  } = useAllOptimizelyExperiments(currentSite.id);

  const {
    isLoading: unbounceExperimentsOptionListIsLoading,
    data: unbounceExperimentsOptionList,
    refetch: refetchUnbounceExperiments,
  } = useAllUnbounceExperiments(currentSite.id);

  const integrationTokensQuery = useIntegrationTokens?.(`${currentSite.id}`);

  // - is this filter supported in the filter chain? (the actionableFilterList variable)
  // - is this filter supported in this context (heatmap, recording, ...)? (the supportedFilterList variable)
  // - is this filter hidden by the "shouldHideLegacyFilters" hook?
  // - is this filter locked by the plan?
  const actionableFilterList = useMemo<SingleFilterConfig[]>(() => {
    return [
      {
        kind: FilterKindEnum.Clicked,
        dateConstraint: START_DATE_OF_CLICKED_DATA_COLLECTION,
      },
      {
        kind: FilterKindEnum.Error,
      },
      {
        kind: FilterKindEnum.VisitedURL,
      },
      {
        kind: FilterKindEnum.LandingURL,
      },
      {
        kind: FilterKindEnum.ExitURL,
      },
      {
        kind: FilterKindEnum.ReferrerURL,
      },
      {
        kind: FilterKindEnum.NewReturningUserKind,
      },
      {
        kind: FilterKindEnum.Country,
        optionList: COUNTRIES,
      },
      {
        kind: FilterKindEnum.Event,
        optionList: eventOptionList,
        optionListIsLoading: eventOptionListIsLoading,
        onDropdownShown: () => {
          if (!eventOptionList || eventOptionList.length === 0) {
            refetchEvents();
          }
        },
      },
      {
        kind: FilterKindEnum.GoogleOptimize,
        optionList: googleExperimentOptionList,
        onDropdownShown: () => {
          if (!googleExperimentOptionList || googleExperimentOptionList.length === 0) {
            refetchGoogleExperiments();
          }
        },
        optionListIsLoading:
          googleExperimentOptionListIsLoading || integrationTokensQuery?.isLoading,
        isIntegrationActive:
          Array.isArray(integrationTokensQuery?.data) &&
          integrationTokensQuery?.data.some(
            (item: IntegrationToken) =>
              item.name === 'GoogleOptimize' &&
              'tag_recordings' in item.settings &&
              item.settings.tag_recordings === true,
          ),
      },
      {
        kind: FilterKindEnum.Optimizely,
        optionList: optimizelyExperimentsOptionList,
        onDropdownShown: () => {
          if (!optimizelyExperimentsOptionList || optimizelyExperimentsOptionList.length === 0) {
            refetchOptimizelyExperiments();
          }
        },
        optionListIsLoading:
          optimizelyExperimentsOptionListIsLoading || integrationTokensQuery?.isLoading,
        isIntegrationActive:
          Array.isArray(integrationTokensQuery?.data) &&
          integrationTokensQuery?.data.some(
            (item: IntegrationToken) =>
              item.name === 'Optimizely' &&
              'tag_recordings' in item.settings &&
              item.settings.tag_recordings === true,
          ),
      },
      {
        kind: FilterKindEnum.Unbounce,
        optionList: unbounceExperimentsOptionList,
        onDropdownShown: () => {
          if (!unbounceExperimentsOptionList || unbounceExperimentsOptionList.length === 0) {
            refetchUnbounceExperiments();
          }
        },
        optionListIsLoading:
          unbounceExperimentsOptionListIsLoading || integrationTokensQuery?.isLoading,
        isIntegrationActive:
          Array.isArray(integrationTokensQuery?.data) &&
          integrationTokensQuery?.data.some(
            (item: IntegrationToken) =>
              item.name === 'Unbounce' &&
              'tag_recordings' in item.settings &&
              item.settings.tag_recordings === true,
          ),
      },
      {
        kind: FilterKindEnum.UserAttribute,
        userAttributeOptionList: Array.from(userAttributeOptionMap.values()),
        onAddMoreUserAttribute: () => {
          // Track clicks that result in this action.
          useTrackResult?.click(
            'Add user attributes link',
            'Filters menu',
            useTrackResult?.getGroupDataForSite(currentSite),
            {
              state_event_was_triggered_on: contextOfUse.page,
              shown_traffic_coverage: useTrafficCoverageResult?.coveragePercentage,
              permission_level: useTrackResult?.getPermissionLevelForSite(currentSite),
            },
          );
          // Open UA settings page.
          window.open(`/settings/user-attributes?site-id=${currentSite.id}`, '_blank');
        },
      },
      {
        kind: FilterKindEnum.SessionDuration,
      },
      {
        kind: FilterKindEnum.PageCount,
      },
      {
        kind: FilterKindEnum.RageClickKind,
      },
      {
        kind: FilterKindEnum.UTurnKind,
      },
      {
        kind: FilterKindEnum.TeamComment,
      },
      {
        kind: FilterKindEnum.UserID,
      },
      {
        kind: FilterKindEnum.DeviceType,
      },
      {
        kind: FilterKindEnum.Browser,
      },
      {
        kind: FilterKindEnum.OS,
      },
      {
        kind: FilterKindEnum.IncomingFeedback,
      },
      {
        kind: FilterKindEnum.NPS,
      },
    ];
  }, [
    eventOptionList,
    eventOptionListIsLoading,
    googleExperimentOptionList,
    googleExperimentOptionListIsLoading,
    integrationTokensQuery,
    optimizelyExperimentsOptionList,
    optimizelyExperimentsOptionListIsLoading,
    unbounceExperimentsOptionList,
    unbounceExperimentsOptionListIsLoading,
    userAttributeOptionMap,
    refetchEvents,
    refetchGoogleExperiments,
    refetchOptimizelyExperiments,
    refetchUnbounceExperiments,
    useTrackResult,
    currentSite,
    contextOfUse.page,
    useTrafficCoverageResult?.coveragePercentage,
  ]);

  const supportedActionableFilterList = useMemo<SingleFilterConfig[]>(() => {
    return actionableFilterList.filter(
      (item) =>
        !LEGACY_FILTERS.has(item.kind) &&
        supportedFilterList.has(item.kind) &&
        (item.kind !== FilterKindEnum.Optimizely || hasOptimizelyFeatureFlag === true) &&
        (item.kind !== FilterKindEnum.Error || hasErrorReportingFeatureFlag),
    );
  }, [
    actionableFilterList,
    hasOptimizelyFeatureFlag,
    hasErrorReportingFeatureFlag,
    supportedFilterList,
  ]);

  const unlockedFilterList = useMemo<Set<FilterKindEnum>>(() => {
    return new Set(
      [...supportedFilterList].filter((item) => {
        if (contextOfUse.productType === ProductType.Feedback) {
          return isAllowedForAsk?.(`filters.${item}`, currentSite) ?? false;
        }

        if (item === FilterKindEnum.Error && isScaleOrScaleTrial) {
          return true;
        }

        return isAllowed?.(`filters.${item}`, currentSite) ?? false;
      }),
    );
  }, [
    supportedFilterList,
    currentSite,
    contextOfUse.productType,
    isAllowed,
    isAllowedForAsk,
    isScaleOrScaleTrial,
  ]);

  const planToUnlockFirstSupportedFilters = useMemo<FeatureGroup | undefined>(() => {
    const groupFeatures = [...supportedFilterList]
      .filter((item) => !unlockedFilterList.has(item))
      .map((item) => getMinimumFeatureGroupForFeature?.(`filters.${item}`) ?? 'scale')
      .sort((a, b) => FeatureGroupEnum[a] - FeatureGroupEnum[b]);
    return groupFeatures?.length > 0 ? groupFeatures[0] : undefined;
  }, [supportedFilterList, unlockedFilterList, getMinimumFeatureGroupForFeature]);

  const finalActionableFilterList = useMemo<SingleFilterConfig[]>(() => {
    return supportedActionableFilterList.map((item) => {
      return {
        ...item,
        isLocked: !unlockedFilterList.has(item.kind),
      };
    });
  }, [unlockedFilterList, supportedActionableFilterList]);

  const finalActionableFilterListWithDateConstraint = useMemo<SingleFilterConfig[]>(() => {
    return finalActionableFilterList.filter((item) => {
      return item.isLocked !== true && item.dateConstraint;
    });
  }, [finalActionableFilterList]);

  const config = useMemo<FilterChainConfig>(() => {
    let showUpsellFooter;
    if (planToUnlockFirstSupportedFilters === 'plus') {
      showUpsellFooter = UpsellFooterEnum.UnlockPlusFilter;
    } else if (planToUnlockFirstSupportedFilters === 'business') {
      showUpsellFooter = UpsellFooterEnum.UnlockBusinessFilter;
    } else if (!isScaleTrial && planToUnlockFirstSupportedFilters === 'scale') {
      showUpsellFooter = UpsellFooterEnum.UnlockScaleFilter;
    }

    const activeDateConstraintList = finalActionableFilterListWithDateConstraint
      .filter((oneFilterConfig) =>
        currentFiltersInUiFormat.filterList.some((item) => item.kind === oneFilterConfig.kind),
      )
      .map((item) => item.dateConstraint);
    const minDateConstaint = maxBy(
      [dateConstraint, ...activeDateConstraintList],
      (item) => item?.minDate,
    );
    return {
      actionableFilterList: finalActionableFilterList,
      showSaveSegmentButton:
        hideSaveSegment !== true &&
        currentFiltersInUiFormat.filterList.length !== 0 &&
        !currentSegment,
      hideAllDateOption,
      hasFilterSavePermission: hasFilterSavePermission ?? false,
      hideAllFilters,
      hideDateRangeFilter,
      hideAddFilters,
      hideClearAllFilters,
      usePrimaryStyledAddFilters,
      useToggleGroupDatePicker,
      dateConstraint: minDateConstaint,
      showUpsellFooter,
      isFunnelStep,
    };
  }, [
    planToUnlockFirstSupportedFilters,
    isScaleTrial,
    finalActionableFilterListWithDateConstraint,
    dateConstraint,
    finalActionableFilterList,
    hideSaveSegment,
    currentFiltersInUiFormat.filterList,
    currentSegment,
    hideAllDateOption,
    hasFilterSavePermission,
    hideAllFilters,
    hideDateRangeFilter,
    hideAddFilters,
    hideClearAllFilters,
    usePrimaryStyledAddFilters,
    useToggleGroupDatePicker,
    isFunnelStep,
  ]);

  const handleSaveSegment = useCallback(
    (newFilterUiFormat) => {
      let newEditedSegment;
      if (lastSegment) {
        newEditedSegment = cloneDeep(lastSegment);
      } else if (segments.length > 0) {
        newEditedSegment = cloneDeep(segments[0]);
      }
      if (newEditedSegment) {
        newEditedSegment.filters = convertFromNewUiToQueryFormat(newFilterUiFormat);
        onSegmentEdited?.(newEditedSegment);
      }
    },
    [lastSegment, segments, onSegmentEdited],
  );

  useEffect(() => {
    if (doNotTrackFiltersChange !== true && useTrackResult) {
      const dataToTrack = {
        globalFilter: currentFiltersInUiFormat,
        segmentId: currentSegment?.id,
        site: currentSite,
        contextOfUse,
      };
      trackGlobalFilterApplied(
        dataToTrack,
        useTrackResult.getGroupDataForSite,
        useTrackResult.track,
      );
    }
    // We want to track each time the filter changes
    // Note that we can't attach this logic to the onChange callback
    // because the filters could be changed by the consumer of the global filters
    // For instance, applying a saved segment in the recording page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFiltersInUiFormat]);

  useEffect(() => {
    if (currentSegment) {
      setLastSegment(currentSegment);
    }
  }, [currentSegment]);

  const handleUpsellClicked = useCallback(
    (kind) => {
      if (kind === 'footer') {
        onUpsellRequest(`${contextOfUse.productType}.filters.menu_footer`, 'menu_footer');
      } else {
        onUpsellRequest(`${contextOfUse.productType}.filters.${kind}`, kind);
      }
    },
    [onUpsellRequest, contextOfUse.productType],
  );

  const handleDropdownFilterMenuShown = useCallback(() => {
    useTrackResult?.click(
      'opened_global_filters_menu',
      'add_filter_button',
      useTrackResult?.getGroupDataForSite(currentSite),
      {
        state_event_was_triggered_on: contextOfUse.page,
        shown_traffic_coverage: useTrafficCoverageResult?.coveragePercentage,
        permission_level: useTrackResult?.getPermissionLevelForSite(currentSite),
      },
    );
  }, [useTrackResult, useTrafficCoverageResult, currentSite, contextOfUse.page]);

  const handleOnChange = useCallback(
    (filtersInUiFormat: GlobalFilter, segmentId?: number) => {
      return onChange(filtersInUiFormat, segmentId);
    },
    [onChange],
  );

  return (
    <FilterChain
      currentValue={currentFiltersInUiFormat}
      onChange={handleOnChange}
      config={config}
      onSaveSegment={handleSaveSegment}
      onUpsellClicked={handleUpsellClicked}
      onDropdownFilterMenuShown={handleDropdownFilterMenuShown}
      setAddFilterTippyInstance={setAddFilterTippyInstance}
      isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
      autoFocusAddFilter={autoFocusAddFilter}
      isScaleTrial={isScaleTrial}
    />
  );
};
